import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  DEFAULT_LINE_SHIFT_INCOMPLETE_BUNDLE_INFO,
  DEFAULT_SELECTED_BCTX_INFO,
  getAllStations,
  getAppIdentifier,
  getBctxItemsOrderWithoutSize,
  getBctxKeysToDisplayMap,
  getBctxViz,
  getBuzKeys,
  getConfigurations,
  getMemberAdditionViewDescriptor,
  getSubjectKeyWithMasterdataListAndConfs,
  getTransactionWipTypeAndSubjectKeyAndAppIdentifier,
  selectAppDetails,
  selectedShift,
  selectedShiftId,
  getBctxConfig,
  getHSKValue,
  getSubjectKey,
  selectAppInfo,
  selectDeviceInfo,
  selectedSubject,
} from '@monorepo/core-data';
import {
  TActionTypeToTotalMap,
  TActivityLogEvent,
  TBctxItemWithoutSizeAndBctxKey,
  TBctxWithMultipleSize,
  TEventStationDetails,
  TEventWhere,
  TForwardBulkEvent,
  TInputAppStore,
  TMemberAdditionEvent,
  TOperationAnsIssue,
  TREQUEST_AppEventsRequestDTO,
  TREQUEST_BusinessContextRequestDTO,
  TREQUEST_RestoreRequest,
  TREQUEST_RestoreRequestForBackedUpBctx,
  TSelectedStationIdAndShiftId,
  TSingleDefectiveEvent,
  TStationDetails,
  TREQUEST_GET_PER_PIECE_LINE_IN_TRANSACTIONS,
  OmittedKeys,
  TRecentOperationAndIssue,
  TSizeMap,
  TPastDaysBackgroundEventsSyncInfo,
  TEventCommonDetails,
  TBCTXKeys,
  TClientWork,
} from '@monorepo/types';
import { BCTXComputationHelper, BuildUtils, EventUtil, PerPieceComputationHelper } from '@monorepo/utils';
import { getEventsBatchCount, getOnlineStatus } from '../metadata/metadata.query';
import * as _ from 'lodash';

export const selectInputAppStore = createFeatureSelector<TInputAppStore>('INPUT_APP_CLIENT_DB');
export const getStore = createSelector(selectInputAppStore, (store) => {
  return store;
});
export const getUserDBLockDetails = createSelector(selectInputAppStore, (store) => {
  return store.userDBLockDetails;
});
export const getBgDBLockDetails = createSelector(selectInputAppStore, (store) => {
  return store.bgDBLockDetails;
});
export const getUserDBDailyCache = createSelector(selectInputAppStore, (store) => {
  return store.userDB.dailyCache;
});
export const getUserDBEvents = createSelector(getUserDBDailyCache, (userDBDailyCache) => {
  return userDBDailyCache.events;
});
export const getBgDBDailyCache = createSelector(selectInputAppStore, (store) => {
  return store.bgDB.dailyCache;
});
export const getBgDBEvents = createSelector(getBgDBDailyCache, (bgDailyCache) => {
  return bgDailyCache.events;
});
export const getBgDBPersistenceCache = createSelector(selectInputAppStore, (store) => {
  return store.bgDB.persistanceCache;
});
export const getBgDBNextEventId = createSelector(getBgDBPersistenceCache, (persistanceCache) => {
  return persistanceCache.nextEventId;
});
export const getUserDBNextCacheResetTimestamp = createSelector(getUserDBDailyCache, (userDBDailyCache) => {
  return userDBDailyCache.nextCacheResetTimestamp;
});
export const getBgDBNextCacheResetTimestamp = createSelector(getBgDBDailyCache, (bgDBDailyCache) => {
  return bgDBDailyCache.nextCacheResetTimestamp;
});
export const getUserDBLineMap = createSelector(getUserDBDailyCache, (userDBDailyCache) => {
  return userDBDailyCache.lineMap;
});
export const getUserDbPPLineMap = createSelector(getUserDBDailyCache, (userDBDailyCache) => {
  return userDBDailyCache.ppLineMap;
});
export const getBgDbPPLineMap = createSelector(getBgDBDailyCache, (userDBDailyCache) => {
  return userDBDailyCache.ppLineMap;
});
export const getBgDBLineMap = createSelector(getBgDBDailyCache, (bgDBDailyCache) => {
  return bgDBDailyCache.lineMap;
});
export const getBctxMinMaxDateTime = createSelector(getUserDBDailyCache, (userDBDailyCache) => {
  return userDBDailyCache.bctxMinMaxDateTime;
});
export const getMergedLineMap = createSelector(getUserDBLineMap, getBgDBLineMap, (userDBLineMap, bgDBLineMap) => {
  return BCTXComputationHelper.mergeUserLineMapWithBgLineMap(userDBLineMap, bgDBLineMap);
});
export const getMergedAggregatedLineMap = createSelector(
  getUserDbPPLineMap,
  getBgDbPPLineMap,
  (userDbPpLineMap, bgDbPpLineMap) => {
    return PerPieceComputationHelper.mergeUserAggregatedLineMapWithBgAggregatedLineMap(userDbPpLineMap, bgDbPpLineMap);
  },
);
export const selectInputAppDB = createSelector(selectInputAppStore, (store) => store.userDB);
export const getUserDBLockDetailsAndNextCacheResetTime = createSelector(
  getUserDBLockDetails,
  getUserDBNextCacheResetTimestamp,
  (userDBLockDetails, nextCacheResetTimestamp) => {
    return {
      lockDetails: userDBLockDetails,
      nextCacheResetTimestamp: nextCacheResetTimestamp,
    };
  },
);

export const getBgDBLockDetailsAndNextCacheResetTime = createSelector(
  getBgDBLockDetails,
  getBgDBNextCacheResetTimestamp,
  (bgDBLockDetails, nextCacheResetTimestamp) => {
    return {
      lockDetails: bgDBLockDetails,
      nextCacheResetTimestamp: nextCacheResetTimestamp,
    };
  },
);
export const getUserDBAndBgDBLockDetails = createSelector(
  getUserDBLockDetails,
  getBgDBLockDetails,
  (userDBLockDetails, bgDBLockDetails) => {
    return {
      bgDBLockDetails: bgDBLockDetails,
      userDBLockDetails: userDBLockDetails,
    };
  },
);
export const getSelectedStationIdAndShiftId = createSelector(
  selectedShiftId,
  selectedSubject,
  (selectedShiftId, selectedSubject) => {
    const stationIdAndShiftId: TSelectedStationIdAndShiftId = {
      stationId: selectedSubject ? selectedSubject.subject : '',
      shiftId: selectedShiftId ? selectedShiftId : '',
    };
    return stationIdAndShiftId;
  },
);

export const getOptionalCurrentLineUserDBLineMap = createSelector(
  getUserDBLineMap,
  getSelectedStationIdAndShiftId,
  (userDBLineMap, getSelectedStationIdAndShiftId) => {
    return userDBLineMap[getSelectedStationIdAndShiftId.stationId];
  },
);
export const getOptionalCurrentLineUserDBLineMapPerPiece = createSelector(
  getOptionalCurrentLineUserDBLineMap,
  getUserDbPPLineMap,
  getSelectedStationIdAndShiftId,
  getAppIdentifier,
  getConfigurations,
  (
    optionalCurrentUserDBLineMap,
    ppUserDBLineMap,
    getSelectedStationIdAndShiftId,
    appIdentifier,
    { aggregatedLineMapForPerPiece },
  ) => {
    if (aggregatedLineMapForPerPiece && BCTXComputationHelper.isPerPieceApp(appIdentifier)) {
      return ppUserDBLineMap[getSelectedStationIdAndShiftId.stationId];
    } else {
      return optionalCurrentUserDBLineMap;
    }
  },
);

export const getOptionalCurrentLineShiftUserDBShiftMap = createSelector(
  getOptionalCurrentLineUserDBLineMap,
  getSelectedStationIdAndShiftId,
  (userDBLineMapItem, getSelectedStationIdAndShiftId) => {
    return userDBLineMapItem && userDBLineMapItem.shiftMap[getSelectedStationIdAndShiftId.shiftId];
  },
);

export const getselectedBctxInfo = createSelector(
  getOptionalCurrentLineUserDBLineMap,
  getOptionalCurrentLineUserDBLineMapPerPiece,
  getAppIdentifier,
  getConfigurations,
  (lineMapItem, ppLineMapItem, app_identifier, { aggregatedLineMapForPerPiece }) => {
    if (aggregatedLineMapForPerPiece && BCTXComputationHelper.isPerPieceApp(app_identifier)) {
      return ppLineMapItem ? ppLineMapItem.selectedBctxInfo : DEFAULT_SELECTED_BCTX_INFO;
    } else {
      return lineMapItem ? lineMapItem.selectedBctxInfo : DEFAULT_SELECTED_BCTX_INFO;
    }
  },
);

export const getClientDBAndBgDB = createSelector(selectInputAppStore, (store) => {
  return { userDB: store.userDB, bgDB: store.bgDB };
});

export const getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift = createSelector(
  selectInputAppStore,
  getOnlineStatus,
  getEventsBatchCount,
  getSelectedStationIdAndShiftId,
  getConfigurations,
  getselectedBctxInfo,
  getAppIdentifier,
  (store, isOnline, batchCount, selectedStationIdAndShiftId, config, selectedBctxInfo, app_identifier) => {
    return {
      userDB: store.userDB,
      bgDB: store.bgDB,
      isOnline,
      batchCount,
      selectedStationIdAndShiftId,
      retryCount: config.retryCount,
      isUserDBLocked: store.userDBLockDetails.isLocked,
      selectedBctxInfo: selectedBctxInfo,
      appIdentifier: app_identifier,
    };
  },
);

export const getMergedLineMapAndSelectedStationIdAndShiftId = createSelector(
  getSelectedStationIdAndShiftId,
  getMergedLineMap,
  (selectedStationIdAndShiftId, mergedLineMap) => {
    return {
      mergedLineMap,
      selectedStationIdAndShiftId,
    };
  },
);

export const getSelectedStationAndShiftDTO = createSelector(
  selectedShift,
  selectedSubject,
  (selectedShift, selectedSubject) => {
    return {
      subject: selectedSubject as unknown,
      shift: selectedShift as unknown,
    } as TEventStationDetails;
  },
);

export const getselectedBctxItemAndOrderKeysAndBctxDisplayMap = createSelector(
  getselectedBctxInfo,
  getBctxItemsOrderWithoutSize,
  getBctxKeysToDisplayMap,
  getBuzKeys,
  getBctxViz,
  (selectedBctxInfo, orderKeys, bctxKeysToDisplayMap, buzKeys, bctxViz) => {
    return {
      selectedBctxInfo: selectedBctxInfo,
      orderKeys: orderKeys,
      bctxKeysToDisplayMap,
      buzKeys: buzKeys,
      bctxViz: bctxViz,
    };
  },
);

export const getDefectiveMetadata = createSelector(
  getBctxItemsOrderWithoutSize,
  getBctxKeysToDisplayMap,
  getBuzKeys,
  getBctxViz,
  getConfigurations,
  getSubjectKey,
  (
    orderKeys,
    bctxKeysToDisplayMap,
    buzKeys,
    bctxViz,
    {
      useOperationSilhouetteMapping,
      useOperationIssueMappingOnly,
      operationsAndIssuesDepartmentId,
      operationsAndIssuesAllDepartmentIds,
    },
    subjectKey,
  ) => {
    return {
      orderKeys: orderKeys,
      bctxKeysToDisplayMap,
      buzKeys: buzKeys,
      bctxViz: bctxViz,
      useOperationSilhouetteMapping,
      useOperationIssueMappingOnly,
      operationsAndIssuesDepartmentId,
      operationsAndIssuesAllDepartmentIds,
      subjectKey,
    };
  },
);

export const getClientBctx = createSelector(
  getMergedLineMap,
  getSelectedStationIdAndShiftId,
  (mergedLineMap, selectedStationIdAndShiftId) => {
    const { stationId, shiftId } = selectedStationIdAndShiftId;
    return mergedLineMap[stationId] && mergedLineMap[stationId].shiftMap[shiftId]
      ? mergedLineMap[stationId].shiftMap[shiftId].clientBctx
      : {};
  },
);
export const getMergedAggLineMap = createSelector(
  getMergedAggregatedLineMap,
  getSelectedStationIdAndShiftId,
  (mergedAggLineMap, selectedStationIdAndShiftId) => {
    const { stationId, shiftId } = selectedStationIdAndShiftId;
    return mergedAggLineMap[stationId] && mergedAggLineMap[stationId].lineInfo[shiftId]
      ? mergedAggLineMap[stationId].lineInfo[shiftId].clientWork
      : ({} as TClientWork);
  },
);

export const getBctxWipMap = createSelector(
  getMergedLineMap,
  getSelectedStationIdAndShiftId,
  (mergedLineMap, selectedStationIdAndShiftId) => {
    const { stationId } = selectedStationIdAndShiftId;
    return mergedLineMap[stationId] ? mergedLineMap[stationId].bctxWipMap : {};
  },
);

export const getBctxWipMapWithOrderKeys = createSelector(
  getBctxWipMap,
  getBctxItemsOrderWithoutSize,
  (wipMap, orderKeys) => {
    return {
      wipMap,
      orderKeys,
    };
  },
);

export const getBusinessContextListing = createSelector(
  selectInputAppDB,
  getSelectedStationIdAndShiftId,
  (selectInputAppDB, selectedStationIdAndShiftId) => {
    const { stationId } = selectedStationIdAndShiftId;
    let businessContextListing: TBctxItemWithoutSizeAndBctxKey[] = [];
    if (selectInputAppDB.dailyCache.lineMap[stationId]) {
      businessContextListing = Object.entries(selectInputAppDB.dailyCache.lineMap[stationId].bctxWipMap).map(
        ([, bctxWipMapItem]) => {
          return {
            list: bctxWipMapItem.bctxItemListWithoutSize,
            bctxKey: bctxWipMapItem.bctxKey,
            isInactive: bctxWipMapItem.isInactive,
          };
        },
      );
    }
    return businessContextListing;
  },
);

export const getPositiveWipBusinessContextListing = createSelector(
  getOptionalCurrentLineUserDBLineMap,
  (lineMapItem) => {
    let businessContextListing: TBctxItemWithoutSizeAndBctxKey[] = [];
    if (lineMapItem) {
      Object.entries(lineMapItem.bctxWipMap).forEach(([, bctxWipMapItem]) => {
        if (Object.values(bctxWipMapItem.sizeToWipMap).some((wipDetails) => wipDetails.wip_qty > 0)) {
          businessContextListing.push({
            list: bctxWipMapItem.bctxItemListWithoutSize,
            bctxKey: bctxWipMapItem.bctxKey,
          });
        }
      });
    }
    return businessContextListing;
  },
);

export const getSizeList = createSelector(
  getMergedLineMap,
  getSelectedStationIdAndShiftId,
  getselectedBctxInfo,
  (mergedLineMap, selectedStationIdAndShiftId, selectedBctxItem) => {
    const { stationId, shiftId } = selectedStationIdAndShiftId;
    const oldBctxKey = selectedBctxItem.bctxInfoWithoutSize.bctxKey;
    const oldBctxSizeMap =
      mergedLineMap[stationId] &&
      mergedLineMap[stationId].shiftMap[shiftId] &&
      mergedLineMap[stationId].shiftMap[shiftId].clientBctx[oldBctxKey]
        ? mergedLineMap[stationId].shiftMap[shiftId].clientBctx[oldBctxKey].sizeMap
        : {};
    console.log({ oldBctxSizeMap });
    if (selectedBctxItem.newBctxForPOSplit) {
      const newBctxKey = selectedBctxItem.newBctxForPOSplit.bctx.bctxKey;
      const newBctxSizeMap =
        mergedLineMap[stationId] &&
        mergedLineMap[stationId].shiftMap[shiftId] &&
        mergedLineMap[stationId].shiftMap[shiftId].clientBctx[newBctxKey]
          ? mergedLineMap[stationId].shiftMap[shiftId].clientBctx[newBctxKey].sizeMap
          : {};
      const intersectionSizes = _.intersection(Object.keys(oldBctxSizeMap), Object.keys(newBctxSizeMap));
      const intersectedSizeMap: TSizeMap = {};
      intersectionSizes.forEach((size) => {
        // Client work on new Bctx
        intersectedSizeMap[size] = newBctxSizeMap[size];
      });
      console.log({ intersectedSizeMap });
      return Object.values(intersectedSizeMap);
    }
    return Object.values(oldBctxSizeMap);
  },
);

export const getBusinessContextDataPayload = createSelector(
  getSubjectKey,
  selectedSubject,
  getBctxConfig,
  getHSKValue,
  getBuzKeys,
  getAppIdentifier,
  (subjectKey, station, bctxConfig, hskValue, buzzKeys, appIdentifier) => {
    const payload: TREQUEST_BusinessContextRequestDTO = {
      subject_key: subjectKey,
      bctx_name: 'unknown',
      hsk: hskValue, //
      context: 'app',
      datetime: new Date().getTime(),
      subjects: station ? [station.subject] : [],
      bctx_type: 'wip',
      compress: false,
      agg_type: 'gradually_consume',
      bctx_keys:
        appIdentifier === 'bundle_qc_v2'
          ? buzzKeys
          : appIdentifier === 'aql_v2' && !buzzKeys.includes('size')
          ? [...buzzKeys, 'size'] // TODO Add size in the buzzkeys
          : buzzKeys.filter((key) => OmittedKeys.every((omittedKey) => omittedKey !== key)),
      server_wip_validation_enabled: true,
      window: [-1, 1], // TODO[Rishikes]: discuss with product or backend [-1,1] => [yesterday, tomorrow]
    };
    if (bctxConfig) {
      payload.bctx_name = bctxConfig.bctx_type === 'plan' ? bctxConfig.plan_name : bctxConfig.bctx_name;
      (payload.bctx_type = bctxConfig.bctx_type), (payload.agg_type = bctxConfig.agg_type);
      payload.server_wip_validation_enabled = bctxConfig.server_wip_validation_enabled || false;
      payload.window = bctxConfig.window;
    }
    return payload;
  },
);

export const getWhoObjectOfEventPayloadWithoutUserInfo = createSelector(
  selectDeviceInfo,
  selectAppInfo,
  (deviceInfo, app) => {
    const who: Omit<TEventWhere, 'ur'> = {
      ad: deviceInfo.deviceid,
      at: app ? app.appInfo.product_type : 'no_app_type',
      an: app ? app.appInfo.app_name : 'no_app_name',
      epf: '',
      av: BuildUtils.APP_VERSION,
    };
    return who;
  },
);
export const getActionInfo = createSelector(selectInputAppStore, (store) => {
  return store.perPieceQcCurrentActionInfoMap;
});
export const getCurrentLineActionInfo = createSelector(
  getActionInfo,
  selectedSubject,
  (perPieceQcCurrentActionInfoMap, subject) => {
    return subject ? perPieceQcCurrentActionInfoMap[subject.subject] : undefined;
  },
);

export const getAppEventsApiPayloadWithoutDateTimeAndRecords = createSelector(
  getSubjectKey,
  getBctxConfig,
  (subject_key, bctxConfig) => {
    const appEventRequest: Omit<TREQUEST_AppEventsRequestDTO, 'datetime' | 'records'> = {
      subject_key: subject_key,
      server_validation:
        bctxConfig?.qty_validation === 'WIP' ? true : bctxConfig?.server_wip_validation_enabled || false,
      qty_validation: bctxConfig?.bctx_type === 'plan' ? 'no_validation' : bctxConfig?.qty_validation || 'WIP',
      wipType: bctxConfig?.bctx_type === 'plan' ? bctxConfig.plan_name : bctxConfig?.bctx_name || 'unknown', // bctx_name
    };
    return appEventRequest;
  },
);

export const getBctxItemAndSizeList = createSelector(getselectedBctxInfo, getBctxWipMap, (selectedBctx, bctxWipMap) => {
  if (selectedBctx.bctxInfoWithoutSize.list.length === 0) {
    const bctx_items: TBctxWithMultipleSize[] = Object.entries(bctxWipMap).map(([, bctxWipMapItem]) => {
      const preSizeLevelBctx = bctxWipMapItem.bctxItemListWithoutSize;
      const sizes = Object.entries(bctxWipMapItem.sizeToWipMap).map(([sizeName]) => sizeName);
      return {
        preSizeLevelBctx,
        sizes,
      };
    });
    return bctx_items;
  } else {
    const bctxKey = selectedBctx.bctxInfoWithoutSize.bctxKey;
    const sizes = bctxWipMap[bctxKey]
      ? Object.entries(bctxWipMap[bctxKey].sizeToWipMap).map(([sizeName]) => sizeName)
      : [];
    return [{ preSizeLevelBctx: selectedBctx.bctxInfoWithoutSize.list, sizes }];
  }
});

export const getRestoreApiPayload = createSelector(
  selectAppInfo,
  selectedSubject,
  getSubjectKey,
  selectedShiftId,
  getBctxConfig,
  getBuzKeys,
  (app, selectedSubject, subjecKey, selectedShiftId, bctxConfig, buzzKeys) => {
    const payload: Omit<TREQUEST_RestoreRequest, 'bctx_items'> = {
      date: BCTXComputationHelper.dashFormateDate(),
      appType: app ? app.appInfo.product_type : 'no_app_type',
      agents: {
        subject: selectedSubject ? selectedSubject.subject : '',
        hsk: selectedSubject ? selectedSubject.hsk : '',
      },
      subjectKey: subjecKey,
      wip_type: bctxConfig ? bctxConfig.bctx_name : 'unknown',
      orderedBctxFields: buzzKeys,
      wip_validation: 'enable',
      shift_id: selectedShiftId || '',
    };
    return payload;
  },
);

export const getAppIdentifierAndBctxMinMaxAndConf = createSelector(
  getAppIdentifier,
  getUserDBDailyCache,
  getConfigurations,
  (appIdentifier, dailyCache, { isWipValidationEnabledLineIn, isPendingWipBasedOnActuals }) => {
    return {
      appIdentifier,
      bctxMinMaxDateTime: dailyCache.bctxMinMaxDateTime,
      isWipValidationEnabledLineIn,
      isPendingWipBasedOnActuals,
    };
  },
);

export const getLineAndShiftRestorePayload = createSelector(
  selectAppInfo,
  selectedSubject,
  getSubjectKey,
  selectedShiftId,
  getBctxConfig,
  getAllStations,
  getBuzKeys,
  getAppIdentifierAndBctxMinMaxAndConf,
  (
    app,
    selectedSubject,
    subjecKey,
    selectedShiftId,
    bctxConfig,
    allStations,
    buzzKeys,
    appIdentifierWithBctxAndConf,
  ) => {
    const bctxKeyWithSize: TBCTXKeys[] = buzzKeys.includes('size') ? buzzKeys : [...buzzKeys, 'size'];
    const appIdentifier = appIdentifierWithBctxAndConf.appIdentifier;
    const bctxMinMaxDateTime = appIdentifierWithBctxAndConf.bctxMinMaxDateTime;
    const wipTypeFromBctxName = bctxConfig ? bctxConfig.bctx_name || null : 'unknown';
    const payload: TREQUEST_RestoreRequestForBackedUpBctx = {
      date: BCTXComputationHelper.dashFormateDate(),
      appType: app ? app.appInfo.product_type : 'no_app_type',
      agentAndShiftForActualsAndWIP: {
        agent: {
          subject: selectedSubject ? selectedSubject.subject : '',
          hsk: selectedSubject ? selectedSubject.hsk : '',
        },
        shift_id: selectedShiftId || '',
      },
      agents:
        appIdentifier === 'line_in_v2'
          ? selectedSubject
            ? [{ subject: selectedSubject.subject, hsk: selectedSubject.hsk }]
            : []
          : allStations?.map(({ subject, hsk }) => ({ subject, hsk })) || [],
      shift_ids: selectedShiftId ? [selectedShiftId] : [],
      subjectKey: subjecKey,
      wip_type:
        appIdentifier === 'line_in_v2'
          ? appIdentifierWithBctxAndConf.isWipValidationEnabledLineIn &&
            !appIdentifierWithBctxAndConf.isPendingWipBasedOnActuals
            ? wipTypeFromBctxName
            : null
          : wipTypeFromBctxName,

      ...(appIdentifier === 'line_in_v2' &&
        bctxMinMaxDateTime.minDate &&
        bctxMinMaxDateTime.maxDate && {
          restore_total_checkins: {
            delivery_date_range: {
              from: bctxMinMaxDateTime.minDate,
              to: bctxMinMaxDateTime.maxDate,
            },
          },
        }),
      orderedBctxFields:
        appIdentifier === 'per_piece_qc_v2' ||
        appIdentifier === 'md_point_per_piece_qc_v2' ||
        appIdentifier === 'per_piece_line_in_v2'
          ? bctxKeyWithSize.filter(
              (ele) => !['search_id', 'bundle_no', 'display_piece_tag', 'audit_disName'].includes(ele),
            )
          : bctxKeyWithSize,
    };
    return payload;
  },
);

export const getBackUpAPIPayload = createSelector(
  selectAppInfo,
  getAllStations,
  getSubjectKey,
  (app, allStations, subjecKey) => {
    return {
      payloadWithoutAgentAndShiftWiseBctx: {
        date: BCTXComputationHelper.dashFormateDate(),
        appType: app ? app.appInfo.product_type : 'no_app_type',
        subjectKey: subjecKey,
      },
      agents: allStations
        ? allStations.map((station) => {
            return {
              subject: station.subject,
              hsk: station.hsk,
            };
          })
        : [],
    };
  },
);

export const getPerPieceLineInTransactionsPayloadMetadata = createSelector(
  selectedSubject,
  getBuzKeys,
  getTransactionWipTypeAndSubjectKeyAndAppIdentifier,
  getConfigurations,
  getBctxConfig,
  (station, buzzKeys, { subjectKey, transactionWipType }, { enablePOSplit }, bctxConfig) => {
    const payload: TREQUEST_GET_PER_PIECE_LINE_IN_TRANSACTIONS = {
      subject_key: subjectKey,
      compressed: false,
      columns: buzzKeys,
      dates: [],
      window: '',
      subject: station ? station.subject : '',
      wip_type: bctxConfig && bctxConfig.station_type === 'WIP_END' ? bctxConfig.bctx_name : transactionWipType,
      ...(enablePOSplit && {
        isBctxGroupAllowed: true,
      }),
    };
    return payload;
  },
);

export const getPerPieceLineInTransactions = createSelector(selectInputAppStore, (store) => {
  return store.perPieceTransactions;
});

export const getReworkedEventsList = createSelector(getUserDBEvents, (events) => {
  return events.list.filter((event) => event.events[0].actionType === 'REWORKED_FROM_DEFECTIVE');
});

export const getSelectedBctxAcutualsMap = createSelector(
  getselectedBctxInfo,
  getClientBctx,
  (selectedBctx, clientBctx) => {
    const actionTypeToTotalActualsMap: TActionTypeToTotalMap = {
      CHECK_IN: 0,
      FTT: 0,
      REJECTED: 0,
      IN_PROGRESS: 0,
      TO_BE_RECTIFIED: 0,
      REWORKED_FROM_DEFECTIVE: 0,
      REJECTED_FROM_DEFECTIVE: 0,
      DEFECTIVE: 0,
    };
    const bctxKey = selectedBctx.newBctxForPOSplit
      ? selectedBctx.newBctxForPOSplit.bctx.bctxKey
      : selectedBctx.bctxInfoWithoutSize.bctxKey;
    if (clientBctx[bctxKey]) {
      Object.entries(clientBctx[bctxKey].sizeMap).map(([, sizeMapItem]) => {
        actionTypeToTotalActualsMap['CHECK_IN'] += sizeMapItem.client_work.CHECK_IN.actuals;
        actionTypeToTotalActualsMap['FTT'] += sizeMapItem.client_work.FTT.actuals;
        actionTypeToTotalActualsMap['REJECTED'] += sizeMapItem.client_work.REJECTED.actuals;
        actionTypeToTotalActualsMap['DEFECTIVE'] += sizeMapItem.client_work.DEFECTIVE.actuals;
        actionTypeToTotalActualsMap['IN_PROGRESS'] += sizeMapItem.client_work.IN_PROGRESS.actuals;
        actionTypeToTotalActualsMap['TO_BE_RECTIFIED'] += sizeMapItem.client_work.TO_BE_RECTIFIED.actuals;
        actionTypeToTotalActualsMap['REWORKED_FROM_DEFECTIVE'] +=
          sizeMapItem.client_work.REWORKED_FROM_DEFECTIVE.actuals;
        actionTypeToTotalActualsMap['REJECTED_FROM_DEFECTIVE'] +=
          sizeMapItem.client_work.REJECTED_FROM_DEFECTIVE.actuals;
      });
    }
    return actionTypeToTotalActualsMap;
  },
);

export const getAllBctxActualsMap = createSelector(getClientBctx, (clientBctx) => {
  const actionTypeToTotalActualsMap: TActionTypeToTotalMap = {
    CHECK_IN: 0,
    FTT: 0,
    REJECTED: 0,
    IN_PROGRESS: 0,
    TO_BE_RECTIFIED: 0,
    REWORKED_FROM_DEFECTIVE: 0,
    REJECTED_FROM_DEFECTIVE: 0,
    DEFECTIVE: 0,
  };
  Object.entries(clientBctx).forEach(([, clientBctxItem]) => {
    Object.entries(clientBctxItem.sizeMap).map(([, sizeMapItem]) => {
      actionTypeToTotalActualsMap['CHECK_IN'] += sizeMapItem.client_work['CHECK_IN'].actuals;
      actionTypeToTotalActualsMap['FTT'] += sizeMapItem.client_work['FTT'].actuals;
      actionTypeToTotalActualsMap['REJECTED'] += sizeMapItem.client_work['REJECTED'].actuals;
      actionTypeToTotalActualsMap['DEFECTIVE'] += sizeMapItem.client_work['DEFECTIVE'].actuals;
      actionTypeToTotalActualsMap['IN_PROGRESS'] += sizeMapItem.client_work['IN_PROGRESS'].actuals;
      actionTypeToTotalActualsMap['TO_BE_RECTIFIED'] += sizeMapItem.client_work['TO_BE_RECTIFIED'].actuals;
      actionTypeToTotalActualsMap['REWORKED_FROM_DEFECTIVE'] +=
        sizeMapItem.client_work['REWORKED_FROM_DEFECTIVE'].actuals;
      actionTypeToTotalActualsMap['REJECTED_FROM_DEFECTIVE'] +=
        sizeMapItem.client_work['REJECTED_FROM_DEFECTIVE'].actuals;
    });
  });
  return actionTypeToTotalActualsMap;
});

export const getAllBctxActualsMapPerPiece = createSelector(
  getClientBctx,
  getMergedAggLineMap,
  getAppIdentifier,
  getConfigurations,
  (clientBctx, aggLineMap, app_identifier, { aggregatedLineMapForPerPiece }) => {
    const actionTypeToTotalActualsMap: TActionTypeToTotalMap = {
      CHECK_IN: 0,
      FTT: 0,
      REJECTED: 0,
      IN_PROGRESS: 0,
      TO_BE_RECTIFIED: 0,
      REWORKED_FROM_DEFECTIVE: 0,
      REJECTED_FROM_DEFECTIVE: 0,
      DEFECTIVE: 0,
    };
    if (aggregatedLineMapForPerPiece && BCTXComputationHelper.isPerPieceApp(app_identifier)) {
      if (Object.keys(aggLineMap).length === 0) {
        return actionTypeToTotalActualsMap;
      }
      actionTypeToTotalActualsMap['CHECK_IN'] += aggLineMap['CHECK_IN'].actuals;
      actionTypeToTotalActualsMap['FTT'] += aggLineMap['FTT'].actuals;
      actionTypeToTotalActualsMap['REJECTED'] += aggLineMap['REJECTED'].actuals;
      actionTypeToTotalActualsMap['DEFECTIVE'] += aggLineMap['DEFECTIVE'].actuals;
      actionTypeToTotalActualsMap['IN_PROGRESS'] += aggLineMap['IN_PROGRESS'].actuals;
      actionTypeToTotalActualsMap['TO_BE_RECTIFIED'] += aggLineMap['TO_BE_RECTIFIED'].actuals;
      actionTypeToTotalActualsMap['REWORKED_FROM_DEFECTIVE'] += aggLineMap['REWORKED_FROM_DEFECTIVE'].actuals;
      actionTypeToTotalActualsMap['REJECTED_FROM_DEFECTIVE'] += aggLineMap['REJECTED_FROM_DEFECTIVE'].actuals;
    } else {
      Object.entries(clientBctx).forEach(([, clientBctxItem]) => {
        Object.entries(clientBctxItem.sizeMap).map(([, sizeMapItem]) => {
          actionTypeToTotalActualsMap['CHECK_IN'] += sizeMapItem.client_work['CHECK_IN'].actuals;
          actionTypeToTotalActualsMap['FTT'] += sizeMapItem.client_work['FTT'].actuals;
          actionTypeToTotalActualsMap['REJECTED'] += sizeMapItem.client_work['REJECTED'].actuals;
          actionTypeToTotalActualsMap['DEFECTIVE'] += sizeMapItem.client_work['DEFECTIVE'].actuals;
          actionTypeToTotalActualsMap['IN_PROGRESS'] += sizeMapItem.client_work['IN_PROGRESS'].actuals;
          actionTypeToTotalActualsMap['TO_BE_RECTIFIED'] += sizeMapItem.client_work['TO_BE_RECTIFIED'].actuals;
          actionTypeToTotalActualsMap['REWORKED_FROM_DEFECTIVE'] +=
            sizeMapItem.client_work['REWORKED_FROM_DEFECTIVE'].actuals;
          actionTypeToTotalActualsMap['REJECTED_FROM_DEFECTIVE'] +=
            sizeMapItem.client_work['REJECTED_FROM_DEFECTIVE'].actuals;
        });
      });
    }
    return actionTypeToTotalActualsMap;
  },
);

export const getActionTypeToTotalActualsMap = createSelector(
  getselectedBctxInfo,
  getAllBctxActualsMap,
  getSelectedBctxAcutualsMap,
  (selectedBctx, allBctxMap, selectedBctxMap) => {
    return selectedBctx.bctxInfoWithoutSize.list.length === 0 ? allBctxMap : selectedBctxMap;
  },
);

export const getStationDetails = createSelector(
  selectedShiftId,
  getSubjectKey,
  selectedSubject,
  getHSKValue,
  (selectedShiftId, subjectKey, station, hsk): TStationDetails => {
    return {
      shiftId: selectedShiftId ? selectedShiftId : '',
      subject: station ? station.subject : '',
      subjectKey: subjectKey,
      hsk: hsk,
      domerole: station ? station.gatek : '',
    };
  },
);

export const getStationDetailsAndSelectedBctxItem = createSelector(
  getStationDetails,
  getselectedBctxInfo,
  (stationDetails, selectedBctxItem) => {
    return { stationDetails, selectedBctxItem };
  },
);

export const getToBeRectifiedListing = createSelector(selectInputAppStore, (selectInputAppStore) => {
  return selectInputAppStore.toBeRectifiedListing;
});

export const getRectifiedBCTXKeysInfo = createSelector(getOptionalCurrentLineShiftUserDBShiftMap, (shiftMapItem) => {
  return shiftMapItem ? shiftMapItem.rectifiedBCTXKeysInfo : {};
});

// export const getAllBctxItemsAcrossAllLines = createSelector(getUserDBLineMap, (lineMap) => {
//   return Object.values(
//     Object.values(lineMap).reduce((acc, curr) => {
//       Object.entries(curr.bctxWipMap).forEach(([key, value]) => {
//         if (!acc[key]) acc[key] = value.bctxItemListWithoutSize;
//       });
//       return acc;
//     }, {} as Record<string, TBctxItemWithoutSize[]>)
//   );
// });

export const getPlanBasedLineMap = createSelector(selectInputAppDB, (selectInputAppDB) => {
  return selectInputAppDB.dailyCache.planBasedLineMap;
});
export const getLineMap = createSelector(selectInputAppDB, (selectInputAppDB) => {
  return selectInputAppDB.dailyCache.lineMap;
});

export const getAllBctxItemsAcrossSelectedStaionId = createSelector(
  selectInputAppDB,
  getSelectedStationIdAndShiftId,
  (selectInputAppDB, selectedStationIdAndShiftId) => {
    return BCTXComputationHelper.getAllBctxItemsAcrossAllLinesForSationId(
      selectInputAppDB.dailyCache.planBasedLineMap,
      selectInputAppDB.dailyCache.lineMap,
      selectedStationIdAndShiftId.stationId,
    );
  },
);

export const getAllBctxItemsAcrossAllLines = createSelector(
  selectInputAppDB,
  getConfigurations,
  getAppIdentifier,
  (selectInputAppDB, { aggregatedLineMapForPerPiece }, app_identifier) => {
    if (aggregatedLineMapForPerPiece && BCTXComputationHelper.isPerPieceApp(app_identifier)) {
      return PerPieceComputationHelper.getAllBctxItemsAcrossAllLinesAsRecordPerPiece(
        selectInputAppDB.dailyCache.ppLineMap,
      );
    } else {
      return Object.values(
        BCTXComputationHelper.getAllBctxItemsAcrossAllLinesAsRecord(
          selectInputAppDB.dailyCache.lineMap,
          selectInputAppDB.dailyCache.planBasedLineMap,
        ),
      );
    }
  },
);

export const getAllBctxItemsAcrossAllLinesAndMasterdataListConfig = createSelector(
  getAllBctxItemsAcrossAllLines,
  getSubjectKeyWithMasterdataListAndConfs,
  getConfigurations,
  (
    bctxAcrossAllLines,
    { silhouetteConf, subjectKey, masterdataList, listConf },
    {
      useOperationSilhouetteMapping,
      operationsAndIssuesDepartmentId,
      operationsAndIssuesAllDepartmentIds,
      useOperationIssueMappingOnly,
    },
  ) => {
    return {
      bctxAcrossAllLines,
      silhouetteConf,
      subjectKey,
      masterdataList,
      listConf,
      useOperationSilhouetteMapping,
      operationsAndIssuesDepartmentId,
      operationsAndIssuesAllDepartmentIds,
      useOperationIssueMappingOnly,
    };
  },
);

export const getEventsArray = createSelector(
  getUserDBEvents,
  getBgDBNextEventId,
  getBgDBEvents,
  getConfigurations,
  (userEvents, nextEventId, bgEvents, config) => {
    return BCTXComputationHelper.getBulkEventsWithStatus(
      userEvents.list,
      bgEvents.list,
      config.retryCount,
      nextEventId,
    )[0];
  },
);

export const getPendingEventsCount = createSelector(
  getEventsArray,
  getBgDBPersistenceCache,
  (eventsArray, { pastDaysList }) => {
    return (
      eventsArray.filter((event) => event.backgroundMetadata.bulkEventStatus === 'YET_TO_BE_SYNCED').length +
      pastDaysList.length
    );
  },
);

export const getPendingEvents = createSelector(
  getEventsArray,
  getBgDBPersistenceCache,
  (eventsArray, { pastDaysList }) => {
    return [
      ...pastDaysList,
      ...eventsArray.filter((event) => event.backgroundMetadata.bulkEventStatus === 'YET_TO_BE_SYNCED'),
    ];
  },
);

export const getTodayAndPastDaysMergedEvents = createSelector(
  getEventsArray,
  getBgDBPersistenceCache,
  (eventsArray, { pastDaysBackgroundEventsSyncInfo }) => {
    const mergedEvents: TPastDaysBackgroundEventsSyncInfo = { ...pastDaysBackgroundEventsSyncInfo };

    eventsArray.forEach((event) => {
      const bctxKey = (event.events[0].eventInfo as TEventCommonDetails)?.bctxDetails?.bctxKey;
      // PBI-2241: SyncTime will always have value as this is called after we sync all pending events
      const syncedTime = event.backgroundMetadata.syncedTime;

      if (bctxKey && syncedTime) {
        mergedEvents[bctxKey] = {
          syncTime: Math.max(mergedEvents[bctxKey]?.syncTime || 0, syncedTime),
        };
      }
    });

    return mergedEvents;
  },
);

export const getPastDaysDefectivesInfo = createSelector(getBgDBPersistenceCache, (bgDBPersistenceCache) => {
  return bgDBPersistenceCache.bulkQcPastDaysDefectives;
});

export const getActivityLogEvents = createSelector(
  getEventsArray,
  getSelectedStationIdAndShiftId,
  (events, { stationId, shiftId }) => {
    return events.filter((event) => {
      return (
        (event.type === 'FORWARD_BULK_EVENT' ||
          event.type === 'UNDO_BULK_EVENT' ||
          event.type === 'MEMBER_ADDITION_EVENT') &&
        EventUtil.hasEventHappenedInStationDetails(event, stationId, shiftId)
      );
    }) as TActivityLogEvent[];
  },
);

export const getPastDayYetToBeSyncedEvents = createSelector(getBgDBPersistenceCache, ({ pastDaysList }) => {
  return pastDaysList.filter((event) => {
    return (
      event.type === 'FORWARD_BULK_EVENT' || event.type === 'UNDO_BULK_EVENT' || event.type === 'MEMBER_ADDITION_EVENT'
    );
  }) as TActivityLogEvent[];
});

export const getLastFttEventForCurrentLineAndShift = createSelector(
  getEventsArray,
  getSelectedStationIdAndShiftId,
  getselectedBctxInfo,
  (eventsArray, getSelectedStationIdAndShiftId, getselectedBctxItem) => {
    const { shiftId, stationId } = getSelectedStationIdAndShiftId;
    return EventUtil.getLastForwardEventWithoutUndo(
      eventsArray,
      stationId,
      shiftId,
      'FTT',
      86400000,
      getselectedBctxItem.bctxInfoWithoutSize.list.length > 0 ? getselectedBctxItem.bctxInfoWithoutSize.bctxKey : null,
    );
  },
);

export const getRecentDefects = createSelector(
  getUserDBEvents,
  getselectedBctxInfo,
  getBctxItemsOrderWithoutSize,
  (events, selectedBctxItem, orderKeys) => {
    const forwardBulkEvents: TForwardBulkEvent[] = events.list.filter(
      (bulkEventItem) => bulkEventItem.type === 'FORWARD_BULK_EVENT',
    ) as TForwardBulkEvent[];
    const recentMetaDataList: TOperationAnsIssue[] = [];
    const operationAndIssueMap = new Map();
    for (var i = forwardBulkEvents.length - 1; i >= 0; i--) {
      for (var j = forwardBulkEvents[i].events.length - 1; j >= 0; j--) {
        const omittedKeys = OmittedKeys;
        const eventKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          orderKeys,
          forwardBulkEvents[i].events[j].eventInfo.bctxDetails.businessContextDetailsWithoutSize.filter(({ k }) => {
            return !omittedKeys.some((key) => key === k);
          }),
        );
        const selectedBctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          orderKeys,
          selectedBctxItem.bctxInfoWithoutSize.list.filter(({ k }) => {
            return !omittedKeys.some((key) => key === k);
          }),
        );
        if (forwardBulkEvents[i].events[j].actionType === 'DEFECTIVE' && eventKey === selectedBctxKey) {
          const defectiveEvent = forwardBulkEvents[i].events[j] as TSingleDefectiveEvent;
          defectiveEvent.recentDefectMetaData.forEach((item) => {
            const combineId = item.operation.id + '_' + item.issue.id + '_' + item.operation.department;
            if (!operationAndIssueMap.has(combineId)) {
              recentMetaDataList.push(item);
              operationAndIssueMap.set(combineId, item);
            }
          });
        }
      }
    }
    return recentMetaDataList;
  },
);

export const getRecentDefectsV2 = createSelector(
  getUserDBEvents,
  getselectedBctxInfo,
  getBctxItemsOrderWithoutSize,
  (events, selectedBctxItem, orderKeys) => {
    const forwardBulkEvents: TForwardBulkEvent[] = events.list.filter(
      (bulkEventItem) => bulkEventItem.type === 'FORWARD_BULK_EVENT',
    ) as TForwardBulkEvent[];
    const recentMetaDataList: TRecentOperationAndIssue[] = [];
    const operationAndIssueMap = new Map();
    for (var i = forwardBulkEvents.length - 1; i >= 0; i--) {
      for (var j = forwardBulkEvents[i].events.length - 1; j >= 0; j--) {
        const omittedKeys = OmittedKeys;
        const eventKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          orderKeys,
          forwardBulkEvents[i].events[j].eventInfo.bctxDetails.businessContextDetailsWithoutSize.filter(({ k }) => {
            return !omittedKeys.some((key) => key === k);
          }),
        );
        const selectedBctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          orderKeys,
          selectedBctxItem.bctxInfoWithoutSize.list.filter(({ k }) => {
            return !omittedKeys.some((key) => key === k);
          }),
        );
        if (forwardBulkEvents[i].events[j].actionType === 'DEFECTIVE' && eventKey === selectedBctxKey) {
          const defectiveEvent = forwardBulkEvents[i].events[j] as TSingleDefectiveEvent;
          defectiveEvent.defectivesInfo.defectives.forEach((item) => {
            const combineId =
              item.operation.category_id + '_' + item.issues.category_id + '_' + item.operation.department;
            if (!operationAndIssueMap.has(combineId)) {
              recentMetaDataList.push({ operation: item.operation, issue: item.issues });
              operationAndIssueMap.set(combineId, item);
            }
          });
        }
      }
    }
    return recentMetaDataList;
  },
);

export const getMemberAdditionList = createSelector(
  getEventsArray,
  getSelectedStationIdAndShiftId,
  (events, selectedStationIdAndShiftId) => {
    const memberAdditionEvents: TMemberAdditionEvent[] = events.filter(
      (item) =>
        item.type === 'MEMBER_ADDITION_EVENT' &&
        EventUtil.hasEventHappenedInStationDetails(
          item,
          selectedStationIdAndShiftId.stationId,
          selectedStationIdAndShiftId.shiftId,
        ),
    ) as TMemberAdditionEvent[];

    const memberAdditionEventsLength = memberAdditionEvents.length;
    if (memberAdditionEventsLength > 0) {
      return memberAdditionEvents[memberAdditionEventsLength - 1].events[0].eventInfo.memberAdditionInfo.list;
    } else return [];
  },
);

export const getMemberAdditionViewDescriptorAndAddedList = createSelector(
  getMemberAdditionList,
  getMemberAdditionViewDescriptor,
  (list, descriptor) => {
    return {
      addedList: list,
      memberAdditionViewDescriptor: descriptor,
    };
  },
);

export const getLastForwardBulkEventBeforeUndoEvent = createSelector(
  getEventsArray,
  getSelectedStationIdAndShiftId,
  (events, { stationId }) => {
    for (let index = events.length - 1; index >= 0; index--) {
      if (events[index].metadata.stationDetails.subject.subject === stationId) {
        if (events[index].type === 'UNDO_BULK_EVENT') return null;
        else if (events[index].type === 'FORWARD_BULK_EVENT') return events[index] as TForwardBulkEvent;
      }
    }
    return null;
  },
);

export const getLastBulkCountEvent = createSelector(
  getEventsArray,
  getSelectedStationIdAndShiftId,
  (events, { stationId }) => {
    for (let index = events.length - 1; index >= 0; index--) {
      if (
        events[index].metadata.stationDetails.subject.subject === stationId &&
        (events[index].type === 'FORWARD_BULK_EVENT' || events[index].type === 'UNDO_BULK_EVENT')
      ) {
        return events[index] as TForwardBulkEvent;
      }
    }
    return null;
  },
);
export const getAllLinesIncompleteBundlesInfo = createSelector(selectInputAppStore, (inputAppInitialState) => {
  return inputAppInitialState.userDB.persistanceCache.incompleteBundlesInfo;
});
export const getIncompleteBundlesInfoForCurrentStationAndShift = createSelector(
  getAllLinesIncompleteBundlesInfo,
  getSelectedStationIdAndShiftId,
  (incompleteBundlesInfo, { shiftId, stationId }) => {
    return incompleteBundlesInfo[stationId] && incompleteBundlesInfo[stationId][shiftId]
      ? incompleteBundlesInfo[stationId][shiftId]
      : DEFAULT_LINE_SHIFT_INCOMPLETE_BUNDLE_INFO;
  },
);

export const hasIncompleteBundlesInfoInitializedForCurrentStationAndShift = createSelector(
  getAllLinesIncompleteBundlesInfo,
  getSelectedStationIdAndShiftId,
  (incompleteBundlesInfo, { shiftId, stationId }) => {
    return incompleteBundlesInfo[stationId] && incompleteBundlesInfo[stationId][shiftId] ? true : false;
  },
);

export const getLogoutAckMetadata = createSelector(
  selectAppDetails,
  getAllStations,
  getHSKValue,
  getPendingEventsCount,
  (appDetails, allSubjects, hsk, pendingEventsCount) => {
    return {
      deviceId: appDetails.deviceId,
      subjectKey: appDetails.factoryId,
      subjects: allSubjects ? allSubjects.map((subject) => subject.subject) : [],
      hsk,
      domrole: allSubjects ? allSubjects[0].gatek : '',
      pendingEventsCount: pendingEventsCount,
    };
  },
);

export const getSubjectKeyAndSelectedSubject = createSelector(
  getSubjectKey,
  selectedSubject,
  (subjectKey, selectedSubject) => {
    return {
      subjectKey,
      selectedSubject,
    };
  },
);

export const getFilteredSizesListForSelectedBctx = createSelector(
  getBctxWipMap,
  getselectedBctxInfo,
  (bctxWipMap, selectedBctxInfo) => {
    return bctxWipMap[selectedBctxInfo.bctxInfoWithoutSize.bctxKey].filteredSizesList;
  },
);

export const getBulkMdPointEvents = createSelector(selectInputAppStore, (inputAppStore) => {
  return inputAppStore.userDB.dailyCache.bulkMdPointEvents;
});
